import React from 'react';
import { Card, Heading, Stack, Input, Button, Text } from '@chakra-ui/react';
import { NavLink, useLoaderData } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import UserPassword from './update-password';
import api from '@/utils/api';
import { User } from '@/lib/types';

export interface UserProps {
  first_name: string;
  last_name: string;
  email: string;
}

export interface PasswordProps {
  new_password: string;
  confirm_password: string;
}

const formSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, { message: 'Email is required' }).email('Invalid email address'),
  config: z.object({
    share_content_email: z.boolean().optional(),
    join_workspace_email: z.boolean().optional(),
    comment_email: z.boolean().optional(),
  }),
});

type FormValues = z.infer<typeof formSchema>;

const UserProfile: React.FC = () => {
  const user = useLoaderData() as User;

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      config: {
        share_content_email: user.config?.share_content_email || false,
        join_workspace_email: user.config?.join_workspace_email || false,
        comment_email: user.config?.comment_email || false,
      },
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await api.put(`users/${user.id}`, {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        config: {
          share_content_email: data.config.share_content_email,
          join_workspace_email: data.config.join_workspace_email,
          comment_email: data.config.comment_email,
        },
      });
      toaster.create({
        type: 'success',
        title: 'Profile updated successfully!',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update user',
      });
    }
  };

  return (
    <Card.Root
      mx={'auto'}
      width={{ base: '90%', sm: '30%' }}
      maxW={'6xl'}
      minW={{ base: '100%', sm: 450 }}
    >
      <Card.Header textAlign={'center'}>
        <Heading size='3xl'>My profile</Heading>
        <Card.Body>
          <form key={user.id} onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={6} align={'flex-start'}>
              <Heading as='h2' size='xl'>
                Profile Information
              </Heading>
              <Field
                label='First name'
                invalid={!!errors.first_name}
                errorText={errors.first_name?.message}
              >
                <Input {...register('first_name')} />
              </Field>
              <Field
                label='Last name'
                invalid={!!errors.last_name}
                errorText={errors.last_name?.message}
              >
                <Input {...register('last_name')} />
              </Field>
              <Field label='Email' invalid={!!errors.email} errorText={errors.email?.message}>
                <Input {...register('email')} type='email' />
              </Field>

              <Controller
                control={control}
                name='config.share_content_email'
                render={({ field }) => (
                  <Field>
                    <Checkbox
                      colorPalette='brand'
                      checked={field.value}
                      onCheckedChange={({ checked }) => field.onChange(checked)}
                    >
                      Share content email
                    </Checkbox>
                  </Field>
                )}
              />
              <Controller
                control={control}
                name='config.join_workspace_email'
                render={({ field }) => (
                  <Field>
                    <Checkbox
                      colorPalette='brand'
                      checked={field.value}
                      onCheckedChange={({ checked }) => field.onChange(checked)}
                    >
                      User joins a workspace
                    </Checkbox>
                  </Field>
                )}
              />
              <Controller
                control={control}
                name='config.comment_email'
                render={({ field }) => (
                  <Field>
                    <Checkbox
                      colorPalette='brand'
                      checked={field.value}
                      onCheckedChange={({ checked }) => field.onChange(checked)}
                    >
                      Comments
                    </Checkbox>
                  </Field>
                )}
              />
              <Button
                colorPalette='brand'
                type={'submit'}
                width={'full'}
                loading={isSubmitting}
                loadingText='Loading...'
              >
                Save
              </Button>
            </Stack>
          </form>
        </Card.Body>
        <Card.Body>
          <UserPassword user={user} />
        </Card.Body>
        <Card.Body mt={5}>
          <Stack gap={6} align={'flex-start'}>
            <Heading as='h2' size='xl'>
              Delete account
            </Heading>
            <Text textAlign={'left'} fontSize={'sm'}>
              Please be aware that once your account is deleted, all associated resources and data
              will be permanently erased. Before proceeding with the deletion, ensure you download
              any data or information you wish to keep. <NavLink to='/#'>Click here</NavLink> to
              delete your account.
            </Text>
          </Stack>
        </Card.Body>
      </Card.Header>
    </Card.Root>
  );
};

export default UserProfile;
